import { useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Box,
  Braces,
  CheckCircle2,
  Code2,
  Database,
  ExternalLink,
  FileCheck2,
  GitBranch,
  Layers3,
  LockKeyhole,
  Microscope,
  Network,
  Scale,
  ShieldCheck,
  Table2,
  TerminalSquare,
  Trophy
} from "lucide-react";
import {
  agentById,
  agentIds,
  denominatorFor,
  instanceBest,
  isSolvedByAny,
  isZeroByAll,
  modeIds,
  modeLabel,
  passRateFor,
  resultFor,
  solvedFor,
  trackFilters,
  trackLabel,
  verify0Data,
  type AgentId,
  type CaseStudy,
  type InstanceResult,
  type Mode,
  type TrackFilter
} from "./data/leaderboard";
import { formatInt, formatUsd } from "./lib/utils";

type InstanceFilter = "all" | "formal" | "python" | "solved" | "zero";

const ACCESS_PASSWORD = "verify0_results";
const ACCESS_STORAGE_KEY = "verify0:access-granted";

const modeColors: Record<Mode, string> = {
  codeproof: "#0ea5e9",
  proof: "#7c3aed"
};

const navItems = [
  ["Why", "why"],
  ["Format", "format"],
  ["Results", "results"],
  ["Audit", "audit"],
  ["Cases", "cases"],
  ["Use", "use"]
] as const;

export default function App() {
  const [hasAccess, setHasAccess] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.sessionStorage.getItem(ACCESS_STORAGE_KEY) === "true";
  });
  const [selectedMode, setSelectedMode] = useState<Mode>("codeproof");
  const [selectedTrack, setSelectedTrack] = useState<TrackFilter>("overall");
  const [instanceFilter, setInstanceFilter] = useState<InstanceFilter>("all");
  const [showInstances, setShowInstances] = useState(true);

  const filteredInstances = useMemo(
    () =>
      verify0Data.instances.filter((row) => {
        if (instanceFilter === "all") {
          return true;
        }
        if (instanceFilter === "solved") {
          return isSolvedByAny(row);
        }
        if (instanceFilter === "zero") {
          return isZeroByAll(row);
        }
        return row.track === instanceFilter;
      }),
    [instanceFilter]
  );

  if (!hasAccess) {
    return (
      <PasswordWall
        onUnlock={() => {
          window.sessionStorage.setItem(ACCESS_STORAGE_KEY, "true");
          setHasAccess(true);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f4f7fb] text-[#07111f] antialiased">
      <SiteNav />
      <main>
        <HeroSection />
        <WhySection />
        <FormatSection />
        <ModesSection />
        <ResultsSection
          selectedMode={selectedMode}
          selectedTrack={selectedTrack}
          setSelectedMode={setSelectedMode}
          setSelectedTrack={setSelectedTrack}
          instanceFilter={instanceFilter}
          setInstanceFilter={setInstanceFilter}
          filteredInstances={filteredInstances}
          showInstances={showInstances}
          setShowInstances={setShowInstances}
        />
        <AuditSection />
        <CaseStudiesSection />
        <CorpusSection />
        <UseSection />
      </main>
      <SiteFooter />
    </div>
  );
}

function PasswordWall({ onUnlock }: { onUnlock: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  return (
    <main
      data-testid="password-wall"
      className="relative grid min-h-screen overflow-hidden place-items-center bg-grid px-4 py-12 text-[#07111f]"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-[#f3f7ff] to-[#fbf8ff]" />
      <form
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white/90 p-6 shadow-panel backdrop-blur sm:p-8"
        onSubmit={(event) => {
          event.preventDefault();
          if (password === ACCESS_PASSWORD) {
            setError("");
            onUnlock();
            return;
          }
          setError("Incorrect password.");
        }}
      >
        <div className="mb-5 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#07111f] text-white">
            <LockKeyhole className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-xl font-semibold leading-tight">Verify0 results preview</h1>
            <p className="mt-1 text-sm text-slate-500">Enter the preview password to continue.</p>
          </div>
        </div>
        <label className="block text-sm font-semibold text-slate-700" htmlFor="verify0-password">
          Password
        </label>
        <input
          id="verify0-password"
          data-testid="access-password-input"
          className="mt-2 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          type="password"
          value={password}
          autoComplete="off"
          autoFocus
          onChange={(event) => setPassword(event.target.value)}
        />
        {error ? (
          <div data-testid="access-error" className="mt-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}
        <button
          className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-lg bg-[#07111f] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#13233a]"
          type="submit"
        >
          Unlock site
        </button>
      </form>
    </main>
  );
}

function SiteNav() {
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <a href="#top" className="flex min-w-0 items-center gap-3 font-semibold text-[#07111f]">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#07111f] text-white shadow-sm">
            <Braces className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block text-base leading-tight">Verify0</span>
            <span className="hidden text-xs font-medium text-slate-500 sm:block">
              repository-scale verified code generation
            </span>
          </span>
        </a>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
          {navItems.map(([label, id]) => (
            <a
              key={id}
              href={`#${id}`}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-[#07111f]"
            >
              {label}
            </a>
          ))}
        </nav>
        <a
          href="#results"
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#07111f] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#13233a]"
        >
          View results
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </header>
  );
}

function HeroSection() {
  const summary = verify0Data.summary;

  return (
    <section id="top" className="relative border-b border-slate-200 bg-grid">
      <div className="absolute inset-0 bg-gradient-to-br from-white via-[#f3f7ff] to-[#fbf8ff]" />
      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-6 lg:pb-20 lg:pt-20">
        <div className="grid gap-8 lg:grid-cols-[0.98fr_1.02fr] lg:items-start">
          <div data-testid="hero" className="rounded-lg border border-slate-200 bg-white/88 p-6 shadow-panel sm:p-8 lg:p-10">
            <div className="mb-5 inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">
              <ShieldCheck className="h-4 w-4" />
              Briefing thesis from the paper
            </div>
            <h1 className="max-w-4xl text-4xl font-semibold leading-[1.04] text-[#07111f] sm:text-5xl lg:text-6xl">
              {summary.headline}
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600">{summary.subheadline}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#07111f] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#13233a]"
                href="#results"
              >
                View results
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-[#07111f] transition hover:border-slate-500"
                href="#format"
              >
                See task format
              </a>
            </div>
          </div>
          <div className="grid gap-4">
            <MetricGrid />
            <HeroCockpit />
          </div>
        </div>
      </div>
    </section>
  );
}

function MetricGrid() {
  const metrics = [
    { label: "Repository-scale instances", value: "35", note: "20 formal-source + 15 Python.", color: "#2563eb" },
    { label: "Formal specifications", value: "1,734", note: "Predicates over curated RepoImpl interfaces.", color: "#0ea5e9" },
    { label: "Lean modules", value: "285", note: "Multi-module projects, not isolated functions.", color: "#7c3aed" },
    { label: "Best full solves", value: "18 / 35", note: "GPT-5.5 xhigh, code-and-proof mode.", color: "#10b981" },
    { label: "Zero-spec hard tail", value: "4", note: "Every evaluated agent closes zero specs.", color: "#f43f5e" },
    { label: "Audit findings fixed", value: "28", note: "Across 8 benchmark instances.", color: "#f59e0b" }
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {metrics.map((metric) => (
        <div key={metric.label} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 h-1 w-12 rounded-full" style={{ backgroundColor: metric.color }} />
          <div className="text-3xl font-semibold leading-none text-[#07111f]">{metric.value}</div>
          <div className="mt-3 text-xs font-bold uppercase text-slate-500">{metric.label}</div>
          <p className="mt-2 text-sm leading-5 text-slate-600">{metric.note}</p>
        </div>
      ))}
    </div>
  );
}

function HeroCockpit() {
  return (
    <div className="relative rounded-lg border border-slate-200 bg-[#07111f] p-5 text-white shadow-panel">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-sky-300">Verification cockpit</div>
          <div className="mt-1 text-xs text-slate-400">Scaffold, slots, clean grader, audit channel</div>
        </div>
        <Network className="h-5 w-5 text-sky-300" />
      </div>
      <div className="grid gap-3 md:grid-cols-[1fr_0.9fr_1fr]">
        <CockpitCard title="Lean repository" icon={GitBranch} items={["Types.lean", "API signatures", "Spec/*.lean"]} />
        <CockpitCard title="RepoImpl" icon={Box} items={["implementation fields", "canonical target", "mode switch"]} emphasis />
        <CockpitCard title="Specifications" icon={FileCheck2} items={["spec_i : RepoImpl -> Prop", "proof_i", "audit_i"]} />
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1fr]">
        <CockpitCard title="Agent slots" icon={Code2} items={["implementation bodies", "proof bodies", "helper lemmas"]} />
        <CockpitCard title="Fresh grading" icon={LockKeyhole} items={["slot extraction", "lake build", "axiom allowlist"]} />
      </div>
      <div className="mt-4 rounded-lg border border-amber-300/30 bg-amber-300/10 p-3 text-sm leading-6 text-amber-50">
        Audit branch: when a spec is impossible, agents can submit machine-checked negative evidence instead of silent failure.
      </div>
    </div>
  );
}

function CockpitCard({
  title,
  items,
  icon: Icon,
  emphasis = false
}: {
  title: string;
  items: string[];
  icon: typeof GitBranch;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        emphasis ? "border-sky-300 bg-sky-300/10" : "border-white/10 bg-white/[0.07]"
      }`}
    >
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-sky-300" />
        {title}
      </div>
      <ul className="grid gap-1.5 text-xs text-slate-300">
        {items.map((item) => (
          <li key={item} className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-300" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhySection() {
  const cards = [
    {
      title: "Function-level verified code generation",
      chips: ["miniCodeProps", "FVAPPS", "VERINA", "CLEVER"],
      body: "Useful for isolated functions, but they miss cross-module dependencies, helper lemmas, and repository-wide invariants.",
      icon: Code2
    },
    {
      title: "Repository-scale proof-only",
      chips: ["RVBench", "VeruSAGE-Bench", "VeriSoftBench"],
      body: "These evaluate proof completion with fixed implementations. Verify0 also asks agents to choose implementations that make proofs tractable.",
      icon: FileCheck2
    },
    {
      title: "Formal mathematics proving",
      chips: ["LeanDojo", "LeanAgent", "APE-Bench"],
      body: "Important for theorem proving, but software verification couples proofs to executable APIs, data structures, and implementation choices.",
      icon: Microscope
    },
    {
      title: "Verify0",
      chips: ["RepoImpl", "clean grader", "audit proofs"],
      body: "Repository-level implementation plus proof synthesis in Lean 4, with fresh-sandbox grading and formal audit evidence.",
      icon: ShieldCheck,
      featured: true
    }
  ];

  return (
    <section id="why" className="border-b border-slate-200 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Why Verify0"
          title="Existing benchmarks stop where verified software starts."
          body="Most verified-code benchmarks focus on isolated functions or proof completion with fixed implementations. Real verified software lives in repositories, where implementation choices, helper lemmas, and specifications interact across files."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {cards.map((card) => (
            <div
              key={card.title}
              className={`rounded-lg border p-5 ${
                card.featured
                  ? "border-blue-200 bg-blue-50/80 shadow-sm"
                  : "border-slate-200 bg-[#f8fafc]"
              }`}
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <card.icon className={card.featured ? "h-5 w-5 text-blue-700" : "h-5 w-5 text-slate-500"} />
                <div className="flex flex-wrap justify-end gap-1.5">
                  {card.chips.map((chip) => (
                    <span key={chip} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600">
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <h3 className="text-xl font-semibold text-[#07111f]">{card.title}</h3>
              <p className="mt-3 leading-7 text-slate-600">{card.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FormatSection() {
  return (
    <section id="format" className="border-b border-slate-200 bg-[#f8fbff] py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Task format"
          title="Every specification is a predicate over an implementation."
          body="Each Verify0 instance exposes a frozen Lean scaffold: data types, helper definitions, API signatures, and formal specifications. Agents fill only implementation and proof slots."
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-[1.04fr_0.96fr] lg:items-start">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-[#07111f] shadow-panel">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                <TerminalSquare className="h-4 w-4 text-sky-300" />
                Simplified Lean shape
              </div>
              <span className="text-xs text-slate-400">RepoImpl {"->"} Prop</span>
            </div>
            <pre className="overflow-x-auto p-5 text-sm leading-7 text-slate-100">
              <code>{`abbrev CreateAccountSig := AccountId -> Ledger -> Ledger
abbrev GetBalanceSig    := AccountId -> Ledger -> Option Balance

structure RepoImpl where
  createAccount : CreateAccountSig
  getBalance    : GetBalanceSig

def spec_create_zero_balance (impl : RepoImpl) : Prop :=
  forall id ledger,
    impl.getBalance id (impl.createAccount id ledger) = some 0

theorem proof_create_zero_balance :
  spec_create_zero_balance canonical := by
  sorry`}</code>
            </pre>
          </div>
          <div className="grid gap-3">
            <PipelineCard
              index="1"
              title="Frozen scaffold"
              items={["types", "helper definitions", "API signatures", "specs"]}
            />
            <PipelineCard
              index="2"
              title="Agent slots"
              items={["implementation bodies", "proof bodies", "helper lemmas"]}
            />
            <PipelineCard
              index="3"
              title="Fresh grading"
              items={["slot extraction", "lake build", "axiom allowlist", "typeclass screening"]}
            />
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900">
              This design supports proof-only, code-and-proof, and audit modes without changing the specifications.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PipelineCard({ index, title, items }: { index: string; title: string; items: string[] }) {
  return (
    <div className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-[44px_1fr]">
      <div className="grid h-11 w-11 place-items-center rounded-lg bg-[#07111f] text-sm font-semibold text-white">{index}</div>
      <div>
        <h3 className="font-semibold text-[#07111f]">{title}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {items.map((item) => (
            <span key={item} className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModesSection() {
  return (
    <section className="border-b border-slate-200 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Evaluation modes"
          title="Two modes, two capabilities."
          body="The paper reports both settings because implementation freedom and proof synthesis measure different parts of repository-scale verification."
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          <ModeExplainer mode="codeproof" />
          <ModeExplainer mode="proof" />
        </div>
        <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-7 text-amber-950">
          Implementation freedom is a double-edged sword. It helps strong agents on Python-derived tasks, where they can rewrite code into proof-friendly Lean, but it can hurt on formal-source tasks when rewrites break inherited invariant structure.
        </div>
      </div>
    </section>
  );
}

function ModeExplainer({ mode }: { mode: Mode }) {
  const meta = verify0Data.modes[mode];
  const bullets =
    mode === "codeproof"
      ? ["implementation slots open", "proof slots open", "canonical points to agent code", "exposes global consistency failures"]
      : ["implementation fixed", "proof slots open", "canonical points to reference code", "separates proof difficulty from implementation choice"];

  return (
    <div className="rounded-lg border border-slate-200 bg-[#f8fafc] p-5">
      <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
        {meta.label}
      </div>
      <h3 className="text-2xl font-semibold text-[#07111f]">{meta.description}</h3>
      <ul className="mt-5 grid gap-2 text-sm text-slate-600">
        {bullets.map((bullet) => (
          <li key={bullet} className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            {bullet}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResultsSection({
  selectedMode,
  selectedTrack,
  setSelectedMode,
  setSelectedTrack,
  instanceFilter,
  setInstanceFilter,
  filteredInstances,
  showInstances,
  setShowInstances
}: {
  selectedMode: Mode;
  selectedTrack: TrackFilter;
  setSelectedMode: (mode: Mode) => void;
  setSelectedTrack: (track: TrackFilter) => void;
  instanceFilter: InstanceFilter;
  setInstanceFilter: (filter: InstanceFilter) => void;
  filteredInstances: readonly InstanceResult[];
  showInstances: boolean;
  setShowInstances: (show: boolean) => void;
}) {
  return (
    <section id="results" className="border-b border-slate-200 bg-[#eef4fb] py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Results"
          title="Frontier agents make progress, but repository-scale verification remains unsolved."
          body="The best evaluated configuration fully solves 18 of 35 repositories. The unsolved tail concentrates on cross-module invariants, custom mathematical theories, and sustained inductive reasoning."
        />
        <ExecutiveOverview />
        <div className="mt-6 grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
          <div className="grid gap-6">
            <ChartPanel
              title="Fully solved instances by agent and mode"
              caption="Figure 2(a): full repository closure out of 35."
            >
              <GroupedBarChart kind="solved" />
            </ChartPanel>
            <ChartPanel
              title="Per-specification pass rate"
              caption="Figure 2(b): aggregate pass rate over all 1,734 specifications."
            >
              <GroupedBarChart kind="passRate" />
            </ChartPanel>
          </div>
          <TrackSplitChart />
        </div>
        <LeaderboardPanel
          selectedMode={selectedMode}
          selectedTrack={selectedTrack}
          setSelectedMode={setSelectedMode}
          setSelectedTrack={setSelectedTrack}
        />
        <FullVsPartialExplainer />
        <InstancePanel
          instanceFilter={instanceFilter}
          setInstanceFilter={setInstanceFilter}
          filteredInstances={filteredInstances}
          showInstances={showInstances}
          setShowInstances={setShowInstances}
        />
        <FindingsGrid />
      </div>
    </section>
  );
}

function ExecutiveOverview() {
  const cards = [
    { label: "Best full solve", value: "18 / 35", note: "GPT-5.5 xhigh, code-and-proof", icon: Trophy, color: "#10b981" },
    { label: "Best per-spec pass rate", value: "62%", note: "GPT-5.5 xhigh, code-and-proof", icon: Table2, color: "#0ea5e9" },
    { label: "Best proof-only", value: "14 / 35", note: "GPT-5.5 xhigh", icon: FileCheck2, color: "#7c3aed" },
    { label: "Audit findings fixed", value: "28", note: "Across 8 benchmark instances", icon: ShieldCheck, color: "#f59e0b" }
  ];

  return (
    <div className="mt-10 rounded-lg border border-slate-200 bg-white p-5 shadow-panel">
      <div className="grid gap-4 md:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className="rounded-lg border border-slate-200 bg-[#f8fafc] p-4">
            <card.icon className="mb-5 h-5 w-5" style={{ color: card.color }} />
            <div className="text-3xl font-semibold leading-none text-[#07111f]">{card.value}</div>
            <div className="mt-3 text-xs font-bold uppercase text-slate-500">{card.label}</div>
            <p className="mt-2 text-sm leading-5 text-slate-600">{card.note}</p>
          </div>
        ))}
      </div>
      <p className="mt-5 border-t border-slate-200 pt-5 text-base leading-7 text-slate-700">
        Verify0 is not saturated. The best evaluated agent solves only 18 of 35 repositories, and the unsolved instances are not random leftovers: they concentrate on cross-module invariants, custom mathematical theories, and long-horizon inductive proof engineering.
      </p>
    </div>
  );
}

function GroupedBarChart({ kind }: { kind: "solved" | "passRate" }) {
  const max = kind === "solved" ? verify0Data.summary.instances : 100;
  const labelFor = (agentId: AgentId, mode: Mode) => {
    if (kind === "solved") {
      const result = resultFor(agentId, mode);
      return `${result.fullySolved} / ${result.totalInstances}`;
    }
    return `${resultFor(agentId, mode).perSpecPassRate}%`;
  };
  const valueFor = (agentId: AgentId, mode: Mode) =>
    kind === "solved" ? resultFor(agentId, mode).fullySolved : resultFor(agentId, mode).perSpecPassRate;

  return (
    <div className="grid gap-5" aria-label={kind === "solved" ? "Fully solved instances chart" : "Per-specification pass-rate chart"}>
      {agentIds.map((agentId) => {
        const agent = agentById(agentId);
        return (
          <div key={agentId} className="grid gap-2 sm:grid-cols-[148px_1fr] sm:items-start">
            <div>
              <div className="text-sm font-semibold text-[#07111f]">{agent.label}</div>
              <div className="text-xs text-slate-500">{agent.harness}</div>
            </div>
            <div className="grid gap-2">
              {modeIds.map((mode) => {
                const value = valueFor(agentId, mode);
                return (
                  <div key={mode} className="grid grid-cols-[98px_1fr_64px] items-center gap-3">
                    <div className="text-xs font-semibold text-slate-500">{verify0Data.modes[mode].label}</div>
                    <div className="h-4 overflow-hidden rounded-lg bg-slate-200">
                      <div
                        className="h-full rounded-lg"
                        style={{ width: `${Math.max(3, (value / max) * 100)}%`, backgroundColor: modeColors[mode] }}
                      />
                    </div>
                    <div className="text-right text-sm font-semibold tabular-nums text-slate-700">{labelFor(agentId, mode)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-4 text-xs text-slate-500">
        <LegendDot color={modeColors.codeproof} label="code-and-proof" />
        <LegendDot color={modeColors.proof} label="proof-only" />
        <span>{kind === "solved" ? "Full repository closure is the primary metric." : "Per-spec rates show partial progress, not full verification."}</span>
      </div>
    </div>
  );
}

function TrackSplitChart() {
  return (
    <div className="rounded-lg border border-[#12233a] bg-[#07111f] p-5 text-white shadow-panel">
      <div className="mb-5 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <h3 className="text-2xl font-semibold">Why the track split matters</h3>
          <p className="mt-2 text-sm leading-6 text-slate-300">Figures 2(c,d): fully solved instances by source track.</p>
        </div>
        <Scale className="h-6 w-6 text-sky-300" />
      </div>
      <div className="grid gap-4">
        {(["formal", "python"] as const).map((track) => {
          const trackInfo = verify0Data.tracks[track];
          const denominator = trackInfo.instances;
          return (
            <div key={track} className="rounded-lg border border-white/10 bg-white/[0.07] p-4">
              <div className="mb-4">
                <div className="text-sm font-semibold text-sky-200">
                  {trackInfo.label} · {trackInfo.shortTitle}
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-300">{trackInfo.interpretation}</p>
              </div>
              <div className="grid gap-3">
                {agentIds.map((agentId) => {
                  const cp = resultFor(agentId, "codeproof").trackSolved[track].solved;
                  const po = resultFor(agentId, "proof").trackSolved[track].solved;
                  return (
                    <div key={agentId} className="grid grid-cols-[104px_1fr_72px] items-center gap-3">
                      <span className="truncate text-xs font-semibold text-slate-300">{agentById(agentId).shortLabel}</span>
                      <div className="grid gap-1.5">
                        <TrackMiniBar value={cp} max={denominator} mode="codeproof" />
                        <TrackMiniBar value={po} max={denominator} mode="proof" />
                      </div>
                      <span className="text-right text-xs tabular-nums text-slate-300">
                        {cp}/{po}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-5 border-t border-white/10 pt-4 text-sm leading-6 text-slate-300">
        Track 1 inherits invariant structure from real Dafny, Verus, and Coq projects. Track 2 comes from Python and often gives strong agents room to rewrite implementations into proof-friendly Lean.
      </p>
    </div>
  );
}

function TrackMiniBar({ value, max, mode }: { value: number; max: number; mode: Mode }) {
  return (
    <div className="h-2 overflow-hidden rounded-lg bg-white/12">
      <div className="h-full rounded-lg" style={{ width: `${Math.max(4, (value / max) * 100)}%`, backgroundColor: modeColors[mode] }} />
    </div>
  );
}

function LeaderboardPanel({
  selectedMode,
  selectedTrack,
  setSelectedMode,
  setSelectedTrack
}: {
  selectedMode: Mode;
  selectedTrack: TrackFilter;
  setSelectedMode: (mode: Mode) => void;
  setSelectedTrack: (track: TrackFilter) => void;
}) {
  const denominator = denominatorFor(selectedTrack);
  const rows = [...agentIds].sort(
    (a, b) => solvedFor(b, selectedMode, selectedTrack) - solvedFor(a, selectedMode, selectedTrack)
  );

  return (
    <div data-testid="leaderboard" className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-panel">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-sm font-semibold text-blue-700">Interactive leaderboard</div>
          <h3 className="mt-1 text-2xl font-semibold text-[#07111f]">Ranking by selected mode and track</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <SegmentedControl
            label="Mode"
            options={modeIds.map((mode) => ({ id: mode, label: verify0Data.modes[mode].label }))}
            selected={selectedMode}
            onSelect={(id) => setSelectedMode(id as Mode)}
          />
          <SegmentedControl
            label="Track"
            options={trackFilters.map((track) => ({ id: track, label: trackLabel(track) }))}
            selected={selectedTrack}
            onSelect={(id) => setSelectedTrack(id as TrackFilter)}
          />
        </div>
      </div>
      <div className="mt-5 overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full min-w-[720px] border-collapse text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Agent</th>
              <th className="px-4 py-3">Mode</th>
              <th className="px-4 py-3">Fully solved</th>
              <th className="px-4 py-3">Per-spec pass rate</th>
              <th className="px-4 py-3">Track 1</th>
              <th className="px-4 py-3">Track 2</th>
              <th className="px-4 py-3 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((agentId) => {
              const agent = agentById(agentId);
              const result = resultFor(agentId, selectedMode);
              const solved = solvedFor(agentId, selectedMode, selectedTrack);
              return (
                <tr key={agentId} className="border-t border-slate-200">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#07111f]">{agent.label}</div>
                    <div className="text-xs text-slate-500">{agent.harness}</div>
                  </td>
                  <td className="px-4 py-3">
                    <ModeBadge mode={selectedMode} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold tabular-nums text-[#07111f]">
                      {solved}/{denominator}
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-lg bg-slate-200">
                      <div
                        className="h-full rounded-lg"
                        style={{ width: `${(solved / denominator) * 100}%`, backgroundColor: modeColors[selectedMode] }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{passRateFor(agentId, selectedMode, selectedTrack)}%</td>
                  <td className="px-4 py-3 tabular-nums">
                    {result.trackSolved.formal.solved}/{result.trackSolved.formal.total}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {result.trackSolved.python.solved}/{result.trackSolved.python.total}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatUsd(result.costUsd)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FullVsPartialExplainer() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-[0.92fr_1.08fr]">
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-amber-700">
          <AlertTriangle className="h-4 w-4" />
          What counts as solved?
        </div>
        <h3 className="text-2xl font-semibold text-[#07111f]">Full repository closure means every specification closes.</h3>
        <p className="mt-3 leading-7 text-slate-600">
          In verified software, the last unproved specifications can encode the property that matters most. Verify0 reports per-spec progress, but treats full repository solve as the headline metric.
        </p>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-[#07111f]">deposit_sc</div>
            <div className="text-sm text-slate-500">GPT-5.5 xhigh, code-and-proof</div>
          </div>
          <ModeBadge mode="codeproof" />
        </div>
        <div className="grid gap-3">
          <div>
            <div className="mb-2 flex justify-between text-sm">
              <span className="font-semibold text-slate-700">Per-spec progress</span>
              <span className="tabular-nums text-slate-600">72 / 79</span>
            </div>
            <div className="h-3 overflow-hidden rounded-lg bg-slate-200">
              <div className="h-full rounded-lg bg-emerald-500" style={{ width: `${(72 / 79) * 100}%` }} />
            </div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-950">
            Full repository status: not solved. The remaining seven specs depend on one missing Merkle-path sibling invariant.
          </div>
        </div>
      </div>
    </div>
  );
}

function InstancePanel({
  instanceFilter,
  setInstanceFilter,
  filteredInstances,
  showInstances,
  setShowInstances
}: {
  instanceFilter: InstanceFilter;
  setInstanceFilter: (filter: InstanceFilter) => void;
  filteredInstances: readonly InstanceResult[];
  showInstances: boolean;
  setShowInstances: (show: boolean) => void;
}) {
  const filterOptions: { id: InstanceFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "formal", label: "Track 1" },
    { id: "python", label: "Track 2" },
    { id: "solved", label: "Solved by any" },
    { id: "zero", label: "Zero by all" }
  ];

  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-panel">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-sm font-semibold text-blue-700">Per-instance evidence</div>
          <h3 className="mt-1 text-2xl font-semibold text-[#07111f]">Benchmark landscape heatmap</h3>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            The matrix reports passed specifications over total specifications for each agent and mode. A check mark indicates full repository closure for that cell.
          </p>
        </div>
        <button
          data-testid="benchmark-detail-toggle"
          type="button"
          onClick={() => setShowInstances(!showInstances)}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-[#07111f] hover:border-slate-500"
        >
          {showInstances ? "Collapse matrix" : "Open matrix"}
        </button>
      </div>
      {showInstances ? (
        <div data-testid="benchmark-detail" className="mt-5">
          <SegmentedControl
            label="Rows"
            options={filterOptions}
            selected={instanceFilter}
            onSelect={(id) => setInstanceFilter(id as InstanceFilter)}
          />
          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1120px] border-collapse text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="sticky left-0 z-10 bg-slate-50 px-3 py-3">Instance</th>
                  <th className="px-3 py-3">Track</th>
                  <th className="px-3 py-3">Specs</th>
                  {agentIds.flatMap((agentId) =>
                    modeIds.map((mode) => (
                      <th key={`${agentId}-${mode}`} className="px-2 py-3 text-center">
                        {agentById(agentId).shortLabel} {verify0Data.modes[mode].shortLabel}
                      </th>
                    ))
                  )}
                  <th className="px-3 py-3">Best</th>
                  <th className="px-3 py-3">Difficulty note</th>
                </tr>
              </thead>
              <tbody>
                {filteredInstances.map((row) => {
                  const best = instanceBest(row);
                  return (
                    <tr key={row.instance} className="border-t border-slate-200">
                      <td className="sticky left-0 z-10 bg-white px-3 py-3">
                        <div className="font-semibold text-[#07111f]">{row.instance}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {row.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="rounded-lg border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {verify0Data.tracks[row.track].label}
                        <div className="text-xs text-slate-400">{row.sourceLanguage}</div>
                      </td>
                      <td className="px-3 py-3 tabular-nums text-slate-600">{row.totalSpecs}</td>
                      {agentIds.flatMap((agentId) =>
                        modeIds.map((mode) => (
                          <td key={`${row.instance}-${agentId}-${mode}`} className="px-2 py-2 text-center">
                            <HeatCell score={row.scores[agentId][mode]} />
                          </td>
                        ))
                      )}
                      <td className="px-3 py-3 tabular-nums text-slate-700">
                        {best.passed}/{best.total}
                        <div className="text-xs text-slate-400">
                          {agentById(best.agentId).shortLabel} {verify0Data.modes[best.mode].shortLabel}
                        </div>
                      </td>
                      <td className="max-w-[260px] px-3 py-3 text-xs leading-5 text-slate-600">{row.note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            The heatmap shows that Verify0 is not uniformly hard. Agents solve local, branch-heavy repositories but fail on repositories requiring reusable lemma libraries, cross-module invariants, and custom mathematical theories.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function HeatCell({ score }: { score: { passed: number; total: number } }) {
  const percent = score.total === 0 ? 0 : score.passed / score.total;
  const solved = score.passed === score.total;
  const color =
    score.passed === 0
      ? "bg-rose-950 text-white"
      : solved
        ? "bg-emerald-100 text-emerald-900 ring-1 ring-emerald-500"
        : percent >= 0.7
          ? "bg-sky-100 text-sky-900"
          : percent >= 0.2
            ? "bg-amber-100 text-amber-950"
            : "bg-rose-100 text-rose-950";

  return (
    <span className={`inline-flex min-w-[58px] items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold tabular-nums ${color}`}>
      {solved ? <CheckCircle2 className="h-3 w-3" /> : null}
      {score.passed}/{score.total}
    </span>
  );
}

function FindingsGrid() {
  const findings = [
    {
      title: "Real verified codebases require deeper invariants.",
      body: "Track 1 inherits invariant structures from Dafny, Verus, and Coq projects, including protocol-level properties and cross-module consistency.",
      icon: Layers3
    },
    {
      title: "More freedom helps only when agents preserve meaning.",
      body: "Code-and-proof helps on Python-derived tasks, but can hurt formal-source tasks when implementation rewrites break repository-wide invariants.",
      icon: GitBranch
    },
    {
      title: "Local case splitting works; sustained induction remains hard.",
      body: "Agents close branchy local specifications but stall when they must invent reusable inductive lemmas or custom mathematical theories.",
      icon: Network
    },
    {
      title: "The last unclosed specs are often the ones that matter.",
      body: "High per-spec progress can still miss the central invariant that makes the whole repository correct.",
      icon: AlertTriangle
    }
  ];

  return (
    <div className="mt-6 grid gap-4 md:grid-cols-2">
      {findings.map((finding) => (
        <div key={finding.title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <finding.icon className="mb-4 h-5 w-5 text-blue-700" />
          <h3 className="text-xl font-semibold text-[#07111f]">{finding.title}</h3>
          <p className="mt-3 leading-7 text-slate-600">{finding.body}</p>
        </div>
      ))}
    </div>
  );
}

function AuditSection() {
  return (
    <section id="audit" className="border-b border-slate-200 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Audit mechanism"
          title="When a spec is impossible, agents can prove that too."
          body="Formal benchmarks can contain subtle errors: missing preconditions, incorrect reference implementations, or inconsistent specifications. Verify0 includes an audit channel where agents can submit machine-checked negative evidence instead of being silently penalized."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {verify0Data.audit.routes.map((route) => (
            <div key={route.title} className="rounded-lg border border-slate-200 bg-[#f8fafc] p-5">
              <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#07111f] text-white">
                <Scale className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold text-[#07111f]">{route.title}</h3>
              <code className="mt-3 block rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
                {route.notation}
              </code>
              <p className="mt-3 leading-6 text-slate-600">{route.description}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
            <div className="mb-5 grid grid-cols-3 gap-3 text-center">
              <AuditMetric value="28" label="findings" />
              <AuditMetric value="8" label="instances" />
              <AuditMetric value="100%" label="fixed" />
            </div>
            <h3 className="text-2xl font-semibold text-[#07111f]">verdict: one byte range contradicted another spec.</h3>
            <p className="mt-3 leading-7 text-slate-700">
              In verdict, one spec required Base64 padding character '=' to return some none, while another reject predicate classified '=' as invalid and required none. GPT-5.5 xhigh produced a joint-unsatisfiability proof; the fix was a one-line exclusion of 0x3D from the reject range.
            </p>
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] border-collapse text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Instance</th>
                  <th className="px-4 py-3">Route</th>
                  <th className="px-4 py-3">Specs</th>
                  <th className="px-4 py-3">Fix kind</th>
                </tr>
              </thead>
              <tbody>
                {verify0Data.audit.distribution.map((row) => (
                  <tr key={row.instance} className="border-t border-slate-200">
                    <td className="px-4 py-3 font-semibold text-[#07111f]">{row.instance}</td>
                    <td className="px-4 py-3 text-slate-600">{row.route}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-600">{row.specs}</td>
                    <td className="px-4 py-3 text-slate-600">{row.fix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function AuditMetric({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-white p-3">
      <div className="text-2xl font-semibold text-[#07111f]">{value}</div>
      <div className="mt-1 text-xs font-bold uppercase text-amber-700">{label}</div>
    </div>
  );
}

function CaseStudiesSection() {
  return (
    <section id="cases" className="border-b border-slate-200 bg-[#f8fbff] py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Case studies"
          title="What repository-scale proof engineering looks like."
          body="The paper’s case studies explain why high partial pass rates can still miss the invariant that closes an end-to-end verification argument."
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          {verify0Data.caseStudies.map((study) => (
            <CaseStudyCard key={study.id} study={study} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudyCard({ study }: { study: CaseStudy }) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-5 md:flex-row md:items-start">
        <div className="md:w-1/2">
          <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
            {study.instance}
          </div>
          <h3 className="text-2xl font-semibold text-[#07111f]">{study.headline}</h3>
          <p className="mt-3 leading-7 text-slate-600">{study.story}</p>
          <div className="mt-5 grid grid-cols-2 gap-2">
            {study.stats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-slate-200 bg-[#f8fafc] p-3">
                <div className="text-lg font-semibold text-[#07111f]">{stat.value}</div>
                <div className="mt-1 text-xs font-bold uppercase text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        <CaseVisual study={study} />
      </div>
    </article>
  );
}

function CaseVisual({ study }: { study: CaseStudy }) {
  if (study.visualType === "contradiction") {
    return (
      <div className="grid flex-1 gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4">
        <SpecCard title="Padding spec" body="charToBits '=' = some none" />
        <div className="text-center text-xs font-semibold uppercase text-rose-700">joint_unsat proof</div>
        <SpecCard title="Reject spec" body="charToBits '=' = none" />
        <div className="rounded-lg bg-white p-3 text-sm text-slate-700">Fix: exclude 0x3D from the reject predicate.</div>
      </div>
    );
  }
  if (study.visualType === "helper-extraction") {
    return (
      <div className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
        {["transfer allowance spec", "balance preservation", "frame condition spec"].map((label) => (
          <div key={label} className="mb-2 rounded-lg border border-emerald-200 bg-white p-3 text-sm font-medium text-emerald-900">
            {label}
          </div>
        ))}
        <div className="mt-4 rounded-lg bg-emerald-700 p-3 text-center text-sm font-semibold text-white">
          reusable helper lemma
        </div>
      </div>
    );
  }
  if (study.visualType === "partial-progress") {
    return (
      <div className="flex-1 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="mb-2 flex justify-between text-sm font-semibold text-amber-950">
          <span>89 / 104 specs closed</span>
          <span>not solved</span>
        </div>
        <div className="h-4 overflow-hidden rounded-lg bg-white">
          <div className="h-full rounded-lg bg-amber-500" style={{ width: `${(89 / 104) * 100}%` }} />
        </div>
        <div className="mt-4 rounded-lg border border-amber-200 bg-white p-3 text-sm leading-6 text-slate-700">
          Remaining specs require global heap-ordering and sortedness invariants.
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 rounded-lg border border-sky-200 bg-sky-50 p-4">
      {["Contract invariants", "Merkle root correctness", "Tree structure lemmas", "Bit-list arithmetic"].map((layer, index) => (
        <div key={layer} className="mb-2 rounded-lg border border-sky-200 bg-white p-3 text-sm font-semibold text-sky-950">
          <span className="mr-2 text-sky-600">{index === 0 ? "top" : "uses"}</span>
          {layer}
        </div>
      ))}
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-950">
        Missing bridge: leaf array grows by one {"->"} Merkle-path siblings evolve correctly.
      </div>
    </div>
  );
}

function SpecCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-white p-3">
      <div className="text-xs font-bold uppercase text-rose-600">{title}</div>
      <code className="mt-2 block text-sm text-slate-700">{body}</code>
    </div>
  );
}

function CorpusSection() {
  return (
    <section className="border-b border-slate-200 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Corpus"
          title="A corpus built from real repositories."
          body="Verify0 contains 35 multi-module instances from Python, Dafny, Verus, and Coq, grouped into formal-source and Python-source tracks."
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          {(["formal", "python"] as const).map((track) => {
            const info = verify0Data.tracks[track];
            return (
              <div key={track} className="rounded-lg border border-slate-200 bg-[#f8fafc] p-5">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-blue-700">{info.label}</div>
                    <h3 className="mt-1 text-2xl font-semibold text-[#07111f]">{info.title}</h3>
                  </div>
                  <Database className="h-6 w-6 text-slate-500" />
                </div>
                <p className="leading-7 text-slate-600">{info.interpretation}</p>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <SmallStat value={String(info.instances)} label="instances" />
                  <SmallStat value={String(info.meanModules)} label="mean modules" />
                  <SmallStat value={String(info.meanApis)} label="mean APIs" />
                  <SmallStat value={String(info.meanSpecs)} label="mean specs" />
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {verify0Data.instances
                    .filter((row) => row.track === track)
                    .slice(0, 7)
                    .map((row) => (
                      <span key={row.instance} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                        {row.instance}
                      </span>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function UseSection() {
  return (
    <section id="use" className="border-b border-slate-200 bg-[#07111f] py-16 text-white sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeader
          eyebrow="Use and extend"
          title="Built to be rerun, audited, and extended."
          body="The grader extracts only permitted slot bodies and checks submissions in a fresh Lean project, with safeguards against definition edits, custom axioms, and typeclass-based proof trivialization."
          inverse
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {verify0Data.ctas.map((cta) => (
            <div key={cta.id} className="rounded-lg border border-white/10 bg-white/[0.07] p-5">
              <CtaIcon id={cta.id} />
              <h3 className="mt-5 text-xl font-semibold text-white">{cta.title}</h3>
              <p className="mt-3 min-h-[84px] leading-7 text-slate-300">{cta.body}</p>
              {cta.href ? (
                <a
                  href={cta.href}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex h-10 items-center gap-2 rounded-lg bg-white px-4 text-sm font-semibold text-[#07111f]"
                >
                  {cta.action}
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : (
                <div className="mt-5 inline-flex h-10 items-center rounded-lg border border-white/15 px-4 text-sm font-semibold text-slate-300">
                  {cta.unavailableLabel}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.07] p-5">
          <div className="grid gap-3 text-sm text-slate-300 sm:grid-cols-3">
            <div>
              <span className="font-semibold text-white">Toolchain:</span> {verify0Data.summary.leanVersion}
            </div>
            <div>
              <span className="font-semibold text-white">Harnesses:</span> {verify0Data.summary.codexVersion}; {verify0Data.summary.claudeCodeVersion}
            </div>
            <div>
              <span className="font-semibold text-white">Models:</span> GPT-5.5 xhigh, GPT-5.5, GPT-5.4, Sonnet 4.6
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaIcon({ id }: { id: string }) {
  const icons: Record<string, typeof BookOpen> = {
    paper: BookOpen,
    code: Code2,
    dataset: Database,
    harness: TerminalSquare
  };
  const Icon = icons[id] ?? BookOpen;
  return (
    <span className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-white/10">
      <Icon className="h-5 w-5 text-sky-300" />
    </span>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-[#07111f] px-4 py-8 text-sm text-slate-400 sm:px-6">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
        <div>
          <div className="font-semibold text-white">Verify0</div>
          <div className="mt-1">{verify0Data.citation}</div>
        </div>
        <div className="text-slate-500">Public metrics are sourced from the paper PDF.</div>
      </div>
    </footer>
  );
}

function ChartPanel({
  title,
  caption,
  children
}: {
  title: string;
  caption: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-panel">
      <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <h3 className="text-2xl font-semibold text-[#07111f]">{title}</h3>
        <p className="text-sm text-slate-500">{caption}</p>
      </div>
      {children}
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  body,
  inverse = false
}: {
  eyebrow: string;
  title: string;
  body: string;
  inverse?: boolean;
}) {
  return (
    <div className="max-w-3xl">
      <div className={`text-sm font-semibold ${inverse ? "text-sky-300" : "text-blue-700"}`}>{eyebrow}</div>
      <h2 className={`mt-3 text-3xl font-semibold leading-tight sm:text-4xl ${inverse ? "text-white" : "text-[#07111f]"}`}>
        {title}
      </h2>
      <p className={`mt-4 text-lg leading-8 ${inverse ? "text-slate-300" : "text-slate-600"}`}>{body}</p>
    </div>
  );
}

function SegmentedControl<T extends string>({
  label,
  options,
  selected,
  onSelect
}: {
  label: string;
  options: { id: T; label: string }[];
  selected: T;
  onSelect: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-bold uppercase text-slate-500">{label}</span>
      <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
        {options.map((option) => (
          <button
            key={option.id}
            data-testid={`${label.toLowerCase().replace(/\s+/g, "-")}-${option.id}`}
            type="button"
            onClick={() => onSelect(option.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
              selected === option.id ? "bg-[#07111f] text-white" : "text-slate-600 hover:bg-white hover:text-[#07111f]"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ModeBadge({ mode }: { mode: Mode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: modeColors[mode] }} />
      {modeLabel(mode)}
    </span>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function SmallStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xl font-semibold text-[#07111f]">{value}</div>
      <div className="mt-1 text-[11px] font-bold uppercase text-slate-500">{label}</div>
    </div>
  );
}
